import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

import type { AllocationRules, RedemptionChannels, RewardsSettingsResponse } from '../hooks/dataHooks';

// ─── Types ───────────────────────────────────────────────────────────────────

type LandingPageState = RewardsSettingsResponse;

type RewardsSettingsState = {
  landingPage: LandingPageState;
};

// ─── Initial state ───────────────────────────────────────────────────────────

const initialState: RewardsSettingsState = {
  landingPage: {
    overallOrganizationBudget: 0,
    allocationRules: {
      recognitionValuePerPostManager: null,
      recognitionValuePerPostEmployee: null,
      monthlySenderReceiverCreditCap: null,
    },
    redemptionChannels: {
      payrollEnabled: false,
      payrollCreditsPerDollar: 0,
      rewardCatalogEnabled: false,
      rewardCreditsPerDollar: 0,
    },
    categories: [],
  },
};

// ─── Slice ───────────────────────────────────────────────────────────────────

const rewardsSettingsSlice = createSlice({
  name: 'rewardsSettings',
  initialState,
  reducers: {
    initializeLandingPage: (state, action: PayloadAction<RewardsSettingsResponse>) => {
      state.landingPage = action.payload;
    },
    updateRedemptionChannels: (state, action: PayloadAction<Partial<RedemptionChannels>>) => {
      state.landingPage.redemptionChannels = {
        ...state.landingPage.redemptionChannels,
        ...action.payload,
      };
    },
    updateOverallOrganizationBudget: (state, action: PayloadAction<number>) => {
      state.landingPage.overallOrganizationBudget = action.payload;
    },
    updateAllocationRules: (state, action: PayloadAction<Partial<AllocationRules>>) => {
      state.landingPage.allocationRules = {
        ...state.landingPage.allocationRules,
        ...action.payload,
      };
    },
    updateCategories: (state, action: PayloadAction<string[]>) => {
      state.landingPage.categories = action.payload;
    },
  },
});

// ─── Exports ─────────────────────────────────────────────────────────────────

export const {
  initializeLandingPage,
  updateRedemptionChannels,
  updateOverallOrganizationBudget,
  updateAllocationRules,
  updateCategories,
} = rewardsSettingsSlice.actions;

export default rewardsSettingsSlice.reducer;

// ─── Selectors ───────────────────────────────────────────────────────────────

type RootState = { rewardsSettings: RewardsSettingsState };

export const useSelectLandingPage = () => useSelector((state: RootState) => state.rewardsSettings.landingPage);

export const useSelectRedemptionChannels = () =>
  useSelector((state: RootState) => state.rewardsSettings.landingPage.redemptionChannels);

export const useSelectOverallOrganizationBudget = () =>
  useSelector((state: RootState) => state.rewardsSettings.landingPage.overallOrganizationBudget);

export const useSelectAllocationRules = () =>
  useSelector((state: RootState) => state.rewardsSettings.landingPage.allocationRules);

export const useSelectCategories = () =>
  useSelector((state: RootState) => state.rewardsSettings.landingPage.categories);
